import React from "react";
import { useQuery } from "react-query";
import { QK } from "../../../base/qk";
import { getMedicineByVisit } from "../../../api/services/medication";
import Loader from "../../../components/Loader/Loader";
import {
  Card,
  Typography,
  Grid,
  Container,
  Box,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  LocalPharmacy,
  Medication as MedicationIcon,
  CalendarToday,
  AccessTime,
  Healing,
  Notes,
  ColorLens,
  Warning,
  Repeat,
  EventRepeat,
  AssignmentTurnedIn,
  Layers,
  SupportAgent,
  ListAlt,
} from "@mui/icons-material";
import NotFound from "../../../components/NotFound/NotFound";
import moment from "moment";

const ACCENT = "#2469F0";

const Row = ({ icon: Icon, label, value, color = ACCENT, renderHtml = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      py: 1,
      px: 0.5,
      borderBottom: "1px solid #F2F4F7",
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Icon sx={{ fontSize: 15, color, flexShrink: 0, mt: 0.3 }} />
    <Typography
      sx={{
        color: "#667085",
        fontWeight: 500,
        fontSize: 12,
        flexShrink: 0,
        width: 72,
        mt: 0.2,
      }}
    >
      {label}
    </Typography>
    {renderHtml && value ? (
      <Box
        dangerouslySetInnerHTML={{ __html: value }}
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 12.5,
          color: "#101828",
          fontWeight: 500,
          lineHeight: 1.55,
          "& p": { margin: "0 0 4px 0" },
          "& p:last-child": { marginBottom: 0 },
          "& ul, & ol": { paddingLeft: "18px", margin: "4px 0" },
          "& li": { marginBottom: "2px", fontSize: 12.5 },
          "& strong, & b": { fontWeight: 700, color: "#101828" },
          "& em, & i": { fontStyle: "italic" },
          "& u": { textDecoration: "underline" },
          "& a": {
            color: ACCENT,
            textDecoration: "underline",
            "&:hover": { color: "#1D4ED8" },
          },
          "& h1, & h2, & h3, & h4": {
            fontWeight: 700,
            color: "#101828",
            margin: "6px 0 3px 0",
            fontSize: "13px",
          },
          "& blockquote": {
            borderLeft: `3px solid ${color}`,
            paddingLeft: "10px",
            margin: "6px 0",
            color: "#475467",
            fontStyle: "italic",
          },
          "& code": {
            bgcolor: "#F2F4F7",
            px: 0.6,
            py: 0.15,
            borderRadius: 0.75,
            fontSize: 11.5,
            fontFamily: "monospace",
          },
        }}
      />
    ) : (
      <Typography
        sx={{
          color: "#101828",
          fontWeight: 600,
          fontSize: 12.5,
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "—"}
      </Typography>
    )}
  </Box>
);

const ChipsRow = ({ icon: Icon, label, items, color = ACCENT }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      py: 1,
      px: 0.5,
      borderBottom: "1px solid #F2F4F7",
    }}
  >
    <Icon sx={{ fontSize: 15, color, flexShrink: 0 }} />
    <Typography
      sx={{
        color: "#667085",
        fontWeight: 500,
        fontSize: 12,
        flexShrink: 0,
        width: 72,
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.5,
        flex: 1,
        minWidth: 0,
      }}
    >
      {items.map((it, idx) => (
        <Box
          key={idx}
          sx={{
            px: 0.9,
            py: 0.25,
            borderRadius: 1,
            bgcolor: `${color}10`,
            color,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          {it}
        </Box>
      ))}
    </Box>
  </Box>
);

const VisitMedication = ({ visitId }) => {
  const { data: visitMedications, isLoading: medLoading } = useQuery(
    [QK.visitMedication, visitId],
    () => getMedicineByVisit(visitId),
    { enabled: !!visitId }
  );

  if (medLoading) return <Loader />;

  const formatDate = (date) =>
    date ? moment(date).format("MMM DD, YY") : "Ongoing";

  const formatTime = (t) => (t ? moment(t, "HH:mm:ss").format("hh:mm A") : null);

  const total = visitMedications?.length || 0;

  return (
    <Container maxWidth={false} sx={{ py: 3, px: { xs: 2.5, sm: 3.5 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 700,
              color: "#101828",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Medications
          </Typography>
          <Typography
            sx={{ fontSize: 12.5, color: "#667085", mt: 0.4, fontWeight: 500 }}
          >
            {total} {total === 1 ? "prescription" : "prescriptions"} linked to
            this visit
          </Typography>
        </Box>

        {total > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.75,
              py: 0.7,
              borderRadius: 10,
              bgcolor: `${ACCENT}0D`,
              border: `1px solid ${ACCENT}25`,
            }}
          >
            <MedicationIcon sx={{ fontSize: 14, color: ACCENT }} />
            <Typography
              sx={{ fontSize: 11.5, fontWeight: 700, color: ACCENT }}
            >
              {total} TOTAL
            </Typography>
          </Box>
        )}
      </Box>

      {total === 0 && (
        <NotFound
          message="No medications found for this visit"
          icon={LocalPharmacy}
        />
      )}

      <Grid container spacing={2.5}>
        {visitMedications?.map((entry) => {
          const med = entry.medicine || {};
          const carePlans = med.care_plan || [];
          const medicineLists = med.medicine_lists || [];
          const weekdays = med.weekdays || [];
          const times = (med.time_data || [])
            .map((t) => formatTime(t.time))
            .filter(Boolean);

          const isControlled = med.controlled_medication;

          return (
            <Grid item xs={12} md={6} lg={4} key={entry.id}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  border: "1px solid #EAECF0",
                  bgcolor: "#FFFFFF",
                  transition: "all 0.24s ease",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    borderColor: "#D0D5DD",
                    boxShadow: "0 14px 32px rgba(16,24,40,0.09)",
                  },
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    pt: 2.25,
                    pb: 1.75,
                    background: isControlled
                      ? "linear-gradient(135deg, #FEF3F2 0%, #FFF7F6 100%)"
                      : "linear-gradient(135deg, #F5F8FF 0%, #FAFBFF 100%)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 2,
                        bgcolor: "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: isControlled
                          ? "0 2px 8px rgba(217,45,32,0.15)"
                          : "0 2px 8px rgba(36,105,240,0.15)",
                      }}
                    >
                      <MedicationIcon
                        sx={{
                          fontSize: 20,
                          color: isControlled ? "#D92D20" : ACCENT,
                        }}
                      />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: "#101828",
                          lineHeight: 1.25,
                          letterSpacing: "-0.01em",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {med.name || med.title || "Unnamed Medicine"}
                      </Typography>
                      {med.medication_type && (
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: "#667085",
                            fontWeight: 500,
                            mt: 0.25,
                          }}
                        >
                          {med.medication_type}
                        </Typography>
                      )}
                    </Box>

                    {isControlled && (
                      <Tooltip title="Controlled medication" arrow>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                            px: 1,
                            py: 0.4,
                            borderRadius: 1.25,
                            bgcolor: "#FFFFFF",
                            border: "1px solid #FECDCA",
                          }}
                        >
                          <Warning
                            sx={{ fontSize: 12, color: "#D92D20" }}
                          />
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#D92D20",
                              letterSpacing: "0.03em",
                            }}
                          >
                            CONTROLLED
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ borderColor: "#EAECF0" }} />

                <Box sx={{ px: 2.5, py: 1, flexGrow: 1 }}>
                  {medicineLists.length > 0 && (
                    <ChipsRow
                      icon={ListAlt}
                      label="Composition"
                      items={medicineLists.map((m) => m.name)}
                      color="#039855"
                    />
                  )}

                  <Row
                    icon={Healing}
                    label="Dose"
                    value={med.dose || "Not specified"}
                    color="#039855"
                  />

                  <Row
                    icon={ColorLens}
                    label="Route"
                    value={med.route_administration}
                    color="#DC6803"
                  />

                  <Row
                    icon={Repeat}
                    label="Frequency"
                    value={
                      med.frequency
                        ? `${med.frequency}${
                            med.every ? ` · Every ${med.every}` : ""
                          }`
                        : "—"
                    }
                    color="#7A5AF8"
                  />

                  <Row
                    icon={CalendarToday}
                    label="Duration"
                    value={`${formatDate(med.start_date)} → ${formatDate(
                      med.end_date
                    )}`}
                    color="#B54708"
                  />

                  {weekdays.length > 0 && (
                    <ChipsRow
                      icon={EventRepeat}
                      label="Days"
                      items={weekdays}
                      color="#0086C9"
                    />
                  )}

                  {times.length > 0 && (
                    <ChipsRow
                      icon={AccessTime}
                      label="Times"
                      items={times}
                      color="#F79009"
                    />
                  )}

                  <Row
                    icon={SupportAgent}
                    label="Assisted"
                    value={med.how_assisted}
                    color="#099250"
                  />

                  {(med.number_of_cards || med.total_slots_remaining) && (
                    <Row
                      icon={Layers}
                      label="Stock"
                      value={`${med.number_of_cards ?? 0} cards · ${
                        med.total_slots_remaining ?? 0
                      } slots`}
                      color="#6938EF"
                    />
                  )}

                  {carePlans.length > 0 && (
                    <ChipsRow
                      icon={AssignmentTurnedIn}
                      label="Care Plan"
                      items={carePlans.map((c) => c.name)}
                      color="#475467"
                    />
                  )}

                  {med.notes && med.notes.replace(/<[^>]*>/g, "").trim() !== "" && (
                    <Row
                      icon={Notes}
                      label="Notes"
                      value={med.notes}
                      color="#0288D1"
                      renderHtml
                    />
                  )}
                </Box>

                <Divider sx={{ borderColor: "#EAECF0" }} />
                <Box
                  sx={{
                    px: 2.5,
                    py: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: "#98A2B3",
                      fontWeight: 500,
                    }}
                  >
                    Updated {moment(med.updated_at).fromNow()}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export default VisitMedication;